import React, { useEffect, useState } from "react";
import {
  Flag,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Gift,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  MessageSquare,
  User,
  Calendar,
  Filter,
  TrendingUp,
  AlertOctagon,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getSurpriseQRReports,
  getSurpriseQRReportStats,
  updateSurpriseQRReportStatus,
} from "../../network/api";

interface Report {
  _id: string;
  surprise_qr_id: string;
  surprise_qr_unique_id: string;
  surprise_qr_title: string;
  reason: string;
  additional_feedback: string;
  user_id: string;
  user_email: string;
  status: "pending" | "reviewed" | "dismissed";
  reviewed_by: string;
  reviewed_at: string;
  notes: string;
  timestamp: string;
  createdAt: string;
}

interface Stats {
  pending: number;
  reviewed: number;
  dismissed: number;
  total: number;
  by_reason: Array<{ _id: string; count: number }>;
}

const SurpriseQRReports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");

  const limit = 12;

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await getSurpriseQRReports({
        status: statusFilter,
        page: currentPage,
        limit,
      });
      setReports(data.reports || []);
      setTotalPages(Math.ceil((data.total || 0) / limit));
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getSurpriseQRReportStats();
      setStats(data.stats);
    } catch (error) {
      console.error("Failed to load stats", error);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [statusFilter, currentPage]);

  const handleUpdateStatus = async (id: string, status: string, notes: string) => {
    setUpdating(true);
    try {
      await updateSurpriseQRReportStatus(id, status, notes);
      toast.success(`Report marked as ${status}`);
      fetchReports();
      fetchStats();
      setShowDetailModal(false);
      setSelectedReport(null);
      setAdminNotes("");
    } catch (error) {
      toast.error("Failed to update report status");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </span>
        );
      case "reviewed":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Reviewed
          </span>
        );
      case "dismissed":
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
            <XCircle className="w-3 h-3 mr-1" />
            Dismissed
          </span>
        );
      default:
        return null;
    }
  };

  const getReasonIcon = (reason: string) => {
    if (reason.includes("Inappropriate")) return <AlertOctagon className="w-4 h-4 text-rose-500" />;
    if (reason.includes("Copyright")) return <MessageSquare className="w-4 h-4 text-indigo-500" />;
    if (reason.includes("Spam")) return <MessageSquare className="w-4 h-4 text-amber-500" />;
    if (reason.includes("Violence")) return <AlertOctagon className="w-4 h-4 text-rose-600" />;
    return <Flag className="w-4 h-4 text-purple-500" />;
  };

  const getReasonColor = (reason: string) => {
    if (reason.includes("Inappropriate")) return "bg-rose-50 border-rose-200";
    if (reason.includes("Copyright")) return "bg-indigo-50 border-indigo-200";
    if (reason.includes("Spam")) return "bg-amber-50 border-amber-200";
    if (reason.includes("Violence")) return "bg-rose-50 border-rose-200";
    return "bg-purple-50 border-purple-200";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
              <Flag className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Surprise QR Reports
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                Manage and review user-reported Surprise QRs
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Pending Reports</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">Awaiting your review</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Reviewed</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{stats.reviewed}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <ThumbsUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">Action taken</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Dismissed</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{stats.dismissed}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <ThumbsDown className="w-6 h-6 text-slate-500" />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">No action needed</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/80">Total Reports</p>
                  <p className="text-3xl font-bold mt-1">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <TrendingUp className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-xs text-white/70">Overall submissions</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-6 p-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filter by:</span>
              <div className="flex gap-2 ml-2">
                {["all", "pending", "reviewed", "dismissed"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 capitalize ${
                      statusFilter === status
                        ? status === "pending"
                          ? "bg-amber-500 text-white shadow-md"
                          : status === "reviewed"
                          ? "bg-emerald-500 text-white shadow-md"
                          : status === "dismissed"
                          ? "bg-slate-500 text-white shadow-md"
                          : "bg-purple-600 text-white shadow-md"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={fetchReports}
              className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-100"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gradient-to-r from-slate-50 to-white">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Surprise QR
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Reported By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                        <p className="text-slate-500 text-sm">Loading reports...</p>
                      </div>
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                          <Flag className="w-8 h-8 text-slate-300" />
                        </div>
                        <p className="text-slate-500 font-medium">No reports found</p>
                        <p className="text-slate-400 text-sm">Try changing the filter</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  reports.map((report) => (
                    <tr key={report._id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                            <Gift className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">
                              {report.surprise_qr_title}
                            </p>
                            <p className="text-xs text-slate-400 font-mono mt-0.5">
                              ID: {report.surprise_qr_unique_id?.slice(-8)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getReasonColor(
                            report.reason
                          )}`}
                        >
                          {getReasonIcon(report.reason)}
                          <span className="text-sm font-medium text-slate-700">
                            {report.reason.length > 35
                              ? report.reason.substring(0, 35) + "..."
                              : report.reason}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-purple-50 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">
                              {report.user_email?.split("@")[0] || "Anonymous"}
                            </p>
                            <p className="text-xs text-slate-400">
                              {report.user_email || "No email"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(report.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <p className="text-sm text-slate-600">
                            {new Date(report.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(report.createdAt).toLocaleTimeString()}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => {
                            setSelectedReport(report);
                            setAdminNotes(report.notes || "");
                            setShowDetailModal(true);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all duration-200 group-hover:shadow-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50/50">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 bg-white border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="text-sm text-slate-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-slate-600 bg-white border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition-all"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedReport && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
                    <Flag className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Report Details</h2>
                    <p className="text-sm text-slate-500 mt-0.5">
                      Review and take action on this report
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedReport(null);
                    setAdminNotes("");
                  }}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <XCircle className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Surprise QR Info */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                  <h3 className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-4">
                    Surprise QR Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">QR Title</p>
                      <p className="text-base font-semibold text-slate-800">
                        {selectedReport.surprise_qr_title}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Unique ID</p>
                      <p className="text-sm font-mono text-slate-600 bg-white/60 px-2 py-1 rounded-lg inline-block">
                        {selectedReport.surprise_qr_unique_id}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Report Info */}
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                    Report Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Reason</p>
                      <div
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${getReasonColor(
                          selectedReport.reason
                        )}`}
                      >
                        {getReasonIcon(selectedReport.reason)}
                        <span className="text-sm font-medium text-slate-700">
                          {selectedReport.reason}
                        </span>
                      </div>
                    </div>
                    {selectedReport.additional_feedback && (
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Additional Feedback</p>
                        <div className="bg-white rounded-xl p-4 border border-slate-200">
                          <p className="text-sm text-slate-600 leading-relaxed">
                            {selectedReport.additional_feedback}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Reported By</p>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700">
                              {selectedReport.user_email?.split("@")[0] || "Anonymous"}
                            </p>
                            <p className="text-xs text-slate-400">
                              {selectedReport.user_email || "No email"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Reported At</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <p className="text-sm text-slate-700">
                            {new Date(selectedReport.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Admin Notes */}
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
                    Admin Notes
                  </h3>
                  <textarea
                    rows={3}
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this report (optional)..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none bg-white text-sm"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() =>
                      handleUpdateStatus(selectedReport._id, "reviewed", adminNotes || "Reviewed by admin")
                    }
                    disabled={updating}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {updating ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Approve & Mark Reviewed
                      </>
                    )}
                  </button>
                  <button
                    onClick={() =>
                      handleUpdateStatus(selectedReport._id, "dismissed", adminNotes || "Dismissed by admin")
                    }
                    disabled={updating}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-slate-500 to-slate-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5" />
                    Dismiss Report
                  </button>
                </div>

                {/* Previous Action Info */}
                {selectedReport.status !== "pending" && (
                  <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-3.5 h-3.5 text-purple-600" />
                      </div>
                      <h3 className="text-sm font-semibold text-purple-800">Previous Action</h3>
                    </div>
                    <div className="space-y-2 pl-8">
                      <p className="text-sm text-purple-700">
                        <span className="font-medium">Status:</span> {selectedReport.status}
                      </p>
                      {selectedReport.reviewed_by && (
                        <p className="text-sm text-purple-700">
                          <span className="font-medium">Reviewed by:</span> {selectedReport.reviewed_by}
                        </p>
                      )}
                      {selectedReport.reviewed_at && (
                        <p className="text-sm text-purple-700">
                          <span className="font-medium">Reviewed at:</span>{" "}
                          {new Date(selectedReport.reviewed_at).toLocaleString()}
                        </p>
                      )}
                      {selectedReport.notes && (
                        <p className="text-sm text-purple-700 mt-2 pt-2 border-t border-purple-100">
                          <span className="font-medium">Notes:</span> {selectedReport.notes}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurpriseQRReports;